import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import localeEnGB from '@angular/common/locales/en-GB';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import { computed, DOCUMENT, effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { TranslocoLoader, TranslocoService } from '@jsverse/transloco';
import { Locale } from 'date-fns';

import { SupportedLanguage } from './api';

registerLocaleData(localeEnGB);
registerLocaleData(localeNl);
registerLocaleData(localeFr);

export const DEFAULT_LOCALE = 'en-GB';
// en-GB first to avoid US formatting in non US countries
export const SUPPORTED_LOCALES: string[] = ['en-GB', 'en-US', 'nl', 'fr'];
// Must match filenames of the files in assets/i18n for *all* embedded apps
export const SUPPORTED_LANGUAGES = Object.values(SupportedLanguage);
export const DEFAULT_LANGUAGE = SupportedLanguage.NL;

export const LANGUAGE_STORAGE_KEY = 'language';

export type TranslationKey = string;

@Injectable({ providedIn: 'root' })
export class LanguageService {
  supportedLanguages: { name: string; code: SupportedLanguage }[] = [
    { name: 'English', code: SupportedLanguage.EN },
    { name: 'Français', code: SupportedLanguage.FR },
    { name: 'Nederlands', code: SupportedLanguage.NL },
  ];
  // false while rendering the app shell at build time, which has no localStorage or navigator
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  language = signal(this.getDefaultLanguage());
  currentLangauge = computed(() => {
    const currentLanguage = this.language();
    return this.supportedLanguages.find((l) => l.code === currentLanguage)!;
  });
  private transloco = inject(TranslocoService);
  private document = inject(DOCUMENT);

  constructor() {
    effect(() => {
      const language = this.language();
      if (this.isBrowser) {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      }
      this.transloco.setActiveLang(language);
      this.document.documentElement.setAttribute('lang', language);
    });
  }

  private getDefaultLanguage(): SupportedLanguage {
    if (!this.isBrowser) {
      return DEFAULT_LANGUAGE;
    }
    const langFromStorage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    let lang: SupportedLanguage | null = null;
    if (langFromStorage) {
      lang = this.getSupportedLanguage(langFromStorage);
    }
    if (!lang) {
      for (const language of navigator.languages) {
        lang = this.getSupportedLanguage(language);
        if (lang) {
          break;
        }
      }
    }
    if (!lang) {
      lang = DEFAULT_LANGUAGE;
    }
    return lang;
  }

  // Only works on initial page load as this can't be changed at runtime anyway
  getCurrentLocale() {
    return this.getSupportedLocale(this.language());
  }

  async getDateFnsLocale(): Promise<Locale> {
    switch (this.language()) {
      case SupportedLanguage.NL:
        return await import('date-fns/locale/nl-BE').then((module) => module.nlBE);
      case SupportedLanguage.EN:
        return await import('date-fns/locale/en-GB').then((module) => module.enGB);
      case SupportedLanguage.FR:
        return await import('date-fns/locale/fr').then((module) => module.fr);
    }
  }

  setLanguage(language: SupportedLanguage) {
    this.language.set(language);
  }

  private getSupportedLanguage(language: string, supportedLanguages = SUPPORTED_LANGUAGES): SupportedLanguage | null {
    language = language.replace('_', '-');
    const split = language.split('-');
    for (const lang of supportedLanguages) {
      if (lang.startsWith(language) || split[0] === lang) {
        return lang;
      }
    }
    return null;
  }

  private getSupportedLocale(lang: string) {
    const userLocale = lang.replace('_', '-');
    if (SUPPORTED_LOCALES.includes(userLocale)) {
      return userLocale;
    }
    const language = userLocale.split('-')[0];
    for (const locale of SUPPORTED_LOCALES) {
      if (locale.startsWith(language)) {
        return locale;
      }
    }
    return DEFAULT_LOCALE;
  }
}

@Injectable({ providedIn: 'root' })
export class TranslocoJsLoader implements TranslocoLoader {
  getTranslation(lang: string) {
    return import(`../i18n/${lang}.json`, { with: { type: 'json' } }).then((a) => a.default);
  }
}

export const httpLanguageInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const languageService = inject(LanguageService);
  const lang = languageService.language();
  if (lang) {
    const clone = req.clone({
      headers: req.headers.set('App-Language', lang),
    });
    return next(clone);
  }
  return next(req);
};
